import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    StyledCTAButton,
    StyledTable,
    StyledTableRow,
    StyledTableCell,
    StyledTableCellRightSpace,
    StyledForgotPasswordLink,
    StyledOpaquePassword,
} from '../../styled';
import { useTranslation } from '~/shared/utils/translation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useValidationSchema } from '../../lib/useValidationSchema';
import { CreateLoginFields, Fields } from '../../models/fields';
import Typography from '~/shared/components/Typography/Typography';
import { Radio } from '~/shared/components';
import { VisuallyHidden } from '~/shared/components/VisuallyHidden';
import { Form, Input, Checkbox, InvalidMessage } from '~/shared/components/form';
import { Grid } from '~/shared/components/Grid';
import { Phone } from '~/shared/components/form/components/form-fields/phone';
import { useFrame } from '~/shared/utils/frame';
import { notifyInfo } from '~/shared/utils/errorBoundary/toast';
import { useLoginDrawer } from '~/features/login/hooks';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useCityByPostCode } from '~/shared/hooks/useCitySearch/useCityByPostCode';
import { dateToString } from '~/utils/date-to-string';
import { LoginDrawerMode } from '~/features/login/hooks/useLoginDrawerState';
import Spinner from '~/shared/components/Spinner/Spinner';
import styles from './SettingsPanel.module.scss';

const DEFAULT_ADDRESS_ID = 'main';

interface SettingsPanelProps {
    className?: string;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ className }) => {
    const frame = useFrame();
    const countryCode = frame.data.culture?.split('-')[1].toUpperCase();
    const { translate } = useTranslation();
    const validationSchema = useValidationSchema();
    const customer = useCustomer();
    const [isReady, setIsReady] = useState(false);
    const [isReadyCustomer, setIsReadyCustomer] = useState(false);
    const customerAddress = customer.addresses?.find((ad) => ad.addressId === DEFAULT_ADDRESS_ID);

    const methods = useForm<CreateLoginFields>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        resolver: yupResolver(validationSchema),
        defaultValues: {},
    });
    const zipCode = methods.watch(Fields.PostalCode);
    const { cityName, hasNoMatchingPostCode, isFetchingCityByPostCode } =
        useCityByPostCode(zipCode);

    const isValid = methods.formState.isValid;
    const isDirty = methods.formState.isDirty;

    const { open } = useLoginDrawer();

    useEffect(() => {
        (async () => {
            await customer.getCustomer();
            setIsReadyCustomer(true);
        })();
    }, []);

    useEffect(() => {
        if (!customer.isInitialized || isReady || customer.isGuest || !isReadyCustomer) return;

        // Use reset() instead of setValue() to properly set default values for dirty checking
        methods.reset({
            [Fields.Email]: String(customer.email) || '',
            [Fields.RepeatEmail]: String(customer.email) || '',
            [Fields.FirstName]: String(customer.firstName) || '',
            [Fields.LastName]: String(customer.lastName) || '',
            [Fields.Address]: customerAddress?.address1 || '',
            [Fields.Gender]: customer.gender?.toString() ?? '',
            [Fields.PostalCode]: customerAddress?.postalCode ?? '',
            [Fields.City]: String(customerAddress?.city ?? ''),
            [Fields.Newsletters]: customer.c_newsletter ?? false,
            [Fields.Mobile]: customerAddress?.phone ?? '',
            [Fields.SocialMedia]: customer?.c_socialMedia ?? false,
            [Fields.Birthday]: customer?.birthday ?? '',
        });
        methods.trigger();
        setIsReady(true);
    }, [customer, isReadyCustomer, methods]);

    useEffect(() => {
        if (isFetchingCityByPostCode) {
            methods.setValue(Fields.City, '');
            return;
        }

        if (cityName) {
            methods.setValue(Fields.City, cityName);
            methods.clearErrors(Fields.PostalCode);
            return;
        }

        if (hasNoMatchingPostCode) {
            methods.setError(Fields.PostalCode, {
                message: translate('message.postNumberValidationError.notFound'),
            });
            methods.setValue(Fields.City, '');
            methods.setValue(Fields.PostalCode, '');
        }
    }, [cityName, hasNoMatchingPostCode, isFetchingCityByPostCode]);

    const handleSubmit = async (payload: CreateLoginFields) => {
        const request = {
            customerId: customer.customerId,
            email: payload.email,
            firstName: payload.firstname,
            lastName: payload.lastname,
            birthday: dateToString(payload.birthday),
            gender: Number(payload.gender),
            address: payload.address,
            postalCode: payload.postalCode,
            city: payload.city,
            phone: payload.mobile,

            c_newsletter: payload.c_newsletter,
            c_socialMedia: payload.c_socialMedia,
            addresses: [
                {
                    addressId: DEFAULT_ADDRESS_ID,
                    countryCode: countryCode!,
                    firstName: payload.firstname,
                    lastName: payload.lastname,
                    address1: payload.address,
                    postalCode: payload.postalCode,
                    city: payload.city,
                    phone: payload.mobile,
                },
            ],
        };

        if (!customer.addresses) {
            await customer.addSavedAddress(request.addresses[0]);
        }

        await customer.updateCustomer(request);

        // Refresh customer data to get updated values from server
        await customer.getCustomer();

        // Reset form with updated customer data, ensuring birthday is in correct format
        const resetPayload = {
            ...payload,
            // Convert birthday Date to YYYY-MM-DD string format for date input
            [Fields.Birthday]:
                payload.birthday && (payload.birthday as unknown) instanceof Date
                    ? dateToString(payload.birthday as unknown as Date)
                    : customer?.birthday ?? '',
        };

        methods.reset(resetPayload);

        notifyInfo(translate('createAccount.profileUpdated'));
    };

    if (!isReady) {
        return (
            <div className={styles.loadingContainer}>
                <Spinner />
            </div>
        );
    }

    return (
        <div className={className}>
            <Form methods={methods} onSubmit={handleSubmit}>
                <Grid flow="row" columns="1fr" rowGap="extrasmall">
                    <fieldset className={styles.fieldset}>
                        <legend className={styles.legend}>
                            <Typography variant="paragraph-M" weight="medium" as="h4">
                                {translate('createAccount.firstnameAndLastname')}
                            </Typography>
                        </legend>
                        <div className={styles.fieldsetContent}>
                            <Input
                                name={Fields.FirstName}
                                label={translate('createAccount.firstName')}
                                required
                            />
                            <Input
                                name={Fields.LastName}
                                label={translate('createAccount.lastName')}
                                required
                            />
                        </div>
                    </fieldset>
                    <fieldset className={styles.fieldset}>
                        <legend className={styles.legend}>
                            <Typography variant="paragraph-M" weight="medium" as="h4">
                                {translate('createAccount.personalInformation')}
                            </Typography>
                        </legend>
                        <div className={styles.fieldsetContent}>
                            <fieldset onChange={() => methods.trigger(Fields.Gender)}>
                                <VisuallyHidden asChild>
                                    <legend>Køn</legend>
                                </VisuallyHidden>
                                <StyledTable>
                                    <StyledTableRow>
                                        <StyledTableCell>
                                            <Radio
                                                aria-label={translate('createAccount.female')}
                                                type="radio"
                                                name={Fields.Gender}
                                                value="2"
                                                defaultChecked={customer.gender === 2}
                                                hideInvalidMessage
                                            />
                                        </StyledTableCell>
                                        <StyledTableCellRightSpace>
                                            {translate('createAccount.female')}
                                        </StyledTableCellRightSpace>
                                        <StyledTableCell>
                                            <Radio
                                                aria-label={translate('createAccount.male')}
                                                type="radio"
                                                name={Fields.Gender}
                                                value="1"
                                                defaultChecked={customer.gender === 1}
                                                hideInvalidMessage
                                            />
                                        </StyledTableCell>
                                        <StyledTableCellRightSpace>
                                            {translate('createAccount.male')}
                                        </StyledTableCellRightSpace>
                                        <StyledTableCell>
                                            <Radio
                                                aria-label={translate('createAccount.genderOther')}
                                                type="radio"
                                                name={Fields.Gender}
                                                value="3"
                                                defaultChecked={customer.gender === 3}
                                                hideInvalidMessage
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {translate('createAccount.genderOther')}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                </StyledTable>
                                <InvalidMessage forField={Fields.Gender} />
                            </fieldset>
                            <Input
                                name={Fields.Birthday}
                                type="date"
                                label={translate('createAccount.birthday')}
                            />
                        </div>
                    </fieldset>
                    <fieldset className={styles.fieldset}>
                        <legend className={styles.legend}>
                            <Typography variant="paragraph-M" weight="medium" as="h4">
                                {translate('createAccount.contactInformation')}
                            </Typography>
                        </legend>
                        <div className={styles.fieldsetContent}>
                            <Phone
                                aria-label={translate('createAccount.mobile')}
                                name={Fields.Mobile}
                                label={translate('createAccount.mobile')}
                                defaultValue={customerAddress?.phone}
                                maxCharacters={8}
                                required
                            />

                            <Input
                                name={Fields.Email}
                                label={translate('createAccount.mail')}
                                required
                            />
                            <Input
                                name={Fields.RepeatEmail}
                                label={translate('createAccount.repeatMail')}
                                required
                            />
                            <Input
                                name={Fields.Address}
                                label={translate('createAccount.address')}
                            />
                            <Grid flow="column" columns="1fr 2fr" columnGap="extrasmall">
                                <Input
                                    name={Fields.PostalCode}
                                    label={translate('createAccount.zip')}
                                    required
                                />
                                <Input
                                    disabled
                                    name={Fields.City}
                                    label={translate('createAccount.city')}
                                    required
                                />
                            </Grid>
                        </div>
                    </fieldset>

                    {!customer.c_externallyAuthenticated && (
                        <fieldset className={styles.fieldset}>
                            <legend className={styles.legend}>
                                <Typography variant="paragraph-M" weight="medium" as="h4">
                                    {translate('createAccount.password')}
                                </Typography>
                            </legend>
                            <div className={styles.fieldsetContent}>
                                <StyledOpaquePassword>
                                    <Input
                                        name={Fields.Password}
                                        type="password"
                                        label={translate('login.password')}
                                        value="password"
                                        disabled
                                    />
                                </StyledOpaquePassword>
                                <StyledForgotPasswordLink
                                    data-testid="change-password-link"
                                    onClick={() => {
                                        open(LoginDrawerMode.CHANGE_PASSWORD);
                                    }}
                                >
                                    {translate('createAccount.changePassword')}
                                </StyledForgotPasswordLink>
                            </div>
                        </fieldset>
                    )}
                    <Grid flow="row">
                        <br />
                        <Typography variant="title-XS" weight="medium" as="h4">
                            {translate('createAccount.getUniqueMemberOffers')}
                        </Typography>
                        <br />
                        <Checkbox
                            name={Fields.Newsletters}
                            label={translate('createAccount.newsletters')}
                            defaultChecked={customer.c_newsletter}
                        />
                    </Grid>
                    <Grid flow="row">
                        <Checkbox
                            name={Fields.SocialMedia}
                            label={translate('createAccount.socialmedia')}
                            defaultChecked={customer.c_socialMedia}
                        />
                    </Grid>
                    {isDirty && (
                        <div className={styles.submitButtonWrapper}>
                            <StyledCTAButton type="submit" variant="primary" disabled={!isValid}>
                                {translate('createAccount.saveChanges')}
                            </StyledCTAButton>
                        </div>
                    )}
                </Grid>
            </Form>
        </div>
    );
};

export default SettingsPanel;
